// Step3ErkekKimlik.tsx
import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import {
	Box,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
	Typography,
	Alert,
	Button,
	CircularProgress
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { FormValues } from "./MainStepper";
import evlendirmeBasvuruAPI from "../../../../../src/apis/OB/SaveApi/EvlendirmeBasvuruAPI";
import kpsGetUserInfoAPI from "../../../../../src/apis/OB/KPS/kpsGetUserInfoAPI";
import UyrukListeApi from "../../../../../src/apis/OB/Get/UyrukListeApi";
import IlIlceListeAPI from "../../../../../src/apis/OB/Get/IlIlceListeAPI";
import CustomTextField from "../../../../components/OB/required-text-field";

dayjs.extend(utc);
dayjs.extend(timezone);

const Step3ErkekKimlik: React.FC = () => {
	const [disabeledInputKPS, setDisabeledInputKPS] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const { values, setFieldValue, setFieldTouched, errors, touched, submitCount } = useFormikContext<FormValues>();
	const step3: any = values.step3 ?? {};

	const [verilisNedeniOptions, setVerilisNedeniOptions] = useState<Array<{ nufusCuzdanVerilisNedeni: string; nufusCuzdanVerilisAciklama: string }>>([]);
	const [diniOptions, setDiniOptions] = useState<Array<{ dini: string; dinAciklama: string }>>([]);
	const [uyrukData, setUyrukData] = useState<Array<{ uyrukKodu: string; uyrukAdi: string }>>([]);
	const [ilIlceListe, setIlIlceListe] = useState<Array<{ ilIlceKodu: string; ilIlceAdi: string }>>([]);

	useEffect(() => {
		const fetchVerilisNedenleri = async () => {
			try {
				const response = await evlendirmeBasvuruAPI.nufusCüzdanVerilis();
				const data = (response as any).data || response;
				if (Array.isArray(data)) setVerilisNedeniOptions(data);
			} catch (error) { console.error("Veriliş nedenleri yüklenirken hata oluştu:", error); }
		};

		const fetchDinler = async () => {
			try {
				const response = await evlendirmeBasvuruAPI.ortDinlerFind();
				const data = (response as any).data || response;
				if (Array.isArray(data)) setDiniOptions(data);
			} catch (error) { console.error("Dinler yüklenirken hata oluştu:", error); }
		};

		const fetchUyruklar = async () => {
			try {
				UyrukListeApi.getUyrukListe()
					.then((res) => setUyrukData(res.data))
					.catch((err) => console.error(err));
			} catch (error) { console.error("Uyruklar yüklenirken hata oluştu:", error); }
		};

		const fetchIlIlce = async () => {
			try {
				IlIlceListeAPI.getIlIlceListe(undefined)
					.then((res) => setIlIlceListe(res.data))
					.catch((err) => console.error(err));
			} catch (error) { console.error("İl/İlçe listesi yüklenirken hata oluştu:", error); }
		};

		fetchVerilisNedenleri();
		fetchDinler();
		fetchUyruklar();
		fetchIlIlce();
	}, []);

	const parseDateToDayjs = (val?: string | null) => {
		if (!val) return null;
		const d = dayjs(val);
		return d.isValid() ? d : null;
	};

	const handleKimlikSorgula = async () => {
		if (!step3.uyrukKodu) {
			setSnackbarMessage("Uyruk zorunludur");
			setSnackbarOpen(true);
			return;
		}

		if (!step3.dogumTarihi) {
			setSnackbarMessage("Doğum tarihi zorunludur");
			setSnackbarOpen(true);
			return;
		}

		if (!step3.tcNo || step3.tcNo.length < 11) {
			setSnackbarMessage("T.C Kimlik Numaranız 11 karakterden küçük olamaz");
			setSnackbarOpen(true);
			return;
		}

		try {
			setSnackbarMessage("");
			setSnackbarOpen(false);
			setIsLoading(true);

			const formattedDate = dayjs(step3.dogumTarihi)
				.tz("Europe/Istanbul")
				.format("YYYY-MM-DD");

			const response = await kpsGetUserInfoAPI.getKPSGetUserInfoListe(
				step3.tcNo,
				formattedDate,
				"1",
				step3.uyrukKodu === "1" ? false : true
			);

			if (
				response?.data?.sorguSonucu &&
				response.data.sorguSonucu.length > 0 &&
				response.data.sorguSonucu[0].tcKimlikNo.toString() === step3.tcNo
			) {
				const userInfo = response.data.sorguSonucu[0];

				// cinsiyet kontrolü
				if (userInfo.temelBilgisi?.cinsiyet?.kod !== 1) {
					setSnackbarMessage("Bu form yalnızca erkek vatandaşlar için kullanılabilir. Lütfen kadın vatandaş bilgilerini ilgili formdan giriniz.");
					setSnackbarOpen(true);
					return;
				}

				setFieldValue("step3.adi", userInfo.temelBilgisi.ad || "");
				setFieldValue("step3.soyadi", userInfo.temelBilgisi.soyad || "");
				setFieldValue("step3.babaAdi", userInfo.temelBilgisi.babaAd || "");
				setFieldValue("step3.anneAdi", userInfo.temelBilgisi.anneAd || "");

				// Doğum yeri için il/ilçe kodunu bulmaya çalış
				if (userInfo.temelBilgisi.dogumYer) {
					const dogumYerText = userInfo.temelBilgisi.dogumYer;
					const foundIl = ilIlceListe.find(il =>
						dogumYerText.toUpperCase().includes(il.ilIlceAdi.toUpperCase())
					);
					if (foundIl) {
						setFieldValue("step3.dogumIlKodu", foundIl.ilIlceKodu);
					}
				}

				setDisabeledInputKPS(true);
				setSnackbarMessage("Kimlik bilgileri başarıyla sorgulandı");
				setSnackbarOpen(true);
			} else {
				setSnackbarMessage("Girilen bilgilerle eşleşen bir kullanıcı bulunamadı");
				setSnackbarOpen(true);
			}
		} catch (error) {
			console.error("KPS sorgulama hatası:", error);
			setSnackbarMessage("KPS sorgulama hatası oluştu");
			setSnackbarOpen(true);
		} finally {
			setIsLoading(false);
		}
	};

	const getError = (name: string) => Boolean(submitCount > 0 && (errors as any)?.step3?.[name]);
	const getHelper = (name: string) => (submitCount > 0 && (errors as any)?.step3?.[name] ? String((errors as any).step3[name]) : "");

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Erkek Kimlik Bilgileri
			</Typography>

			<Alert severity="info" sx={{ mb: 3 }}>
				Eski kimlik kullanan vatandaşlar seri ve sıra numarasını sırasıyla arada boşluk bırakmadan ‘Nüfus Cüzdanı Seri No’ alanına yazmalıdır.
			</Alert>

			{snackbarOpen && snackbarMessage && (
				<Alert
					severity={snackbarMessage.includes("başarıyla") ? "success" : "error"}
					sx={{ mb: 3 }}
					onClose={() => setSnackbarOpen(false)}
				>
					{snackbarMessage}
				</Alert>
			)}

			<Grid container spacing={2}>

				<Grid item xs={12} md={3}>
					<FormControl fullWidth error={getError("uyrukKodu")} required size="small">
						<InputLabel id="uyruk-label">Uyruk</InputLabel>
						<Select
							labelId="uyruk-label"
							label="Uyruk"
							value={step3.uyrukKodu ?? "1"}
							onChange={(e) => setFieldValue("step3.uyrukKodu", e.target.value)}
						>
							{uyrukData.map((o) => (
								<MenuItem key={o.uyrukKodu} value={o.uyrukKodu}>
									{o.uyrukAdi}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>{getHelper("uyrukKodu")}</FormHelperText>
					</FormControl>
				</Grid>

				{/* Row 1 */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Vatandaşlık No"
						required
						fullWidth
						value={step3.tcNo ?? ""}
						onChange={(e) => setFieldValue("step3.tcNo", e.target.value)}
						error={getError("tcNo")}
						helperText={getHelper("tcNo")}
						inputProps={{ maxLength: 11 }}
					/>
				</Grid>

				<Grid item xs={12} md={3}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label="Doğum Tarihi"
							value={parseDateToDayjs(step3.dogumTarihi)}
							onChange={(newValue: Dayjs | null) => {
								setFieldValue("step3.dogumTarihi", newValue ? newValue.format("YYYY-MM-DD") : "");
							}}
							format="DD/MM/YYYY"
							slotProps={{
								textField: {
									fullWidth: true,
									required: true,
									error: getError("dogumTarihi"),
									helperText: getHelper("dogumTarihi"),
									size: "small"
								}
							}}
						/>
					</LocalizationProvider>
				</Grid>

				<Grid item xs={12} md={3}>
					<Button
						variant="contained"
						color="error"
						fullWidth
						disabled={disabeledInputKPS || isLoading}
						onClick={handleKimlikSorgula}
						size="small"
					>
						{isLoading ? (
							<CircularProgress size={24} color="inherit" />
						) : (
							"Kimlik Sorgula"
						)}
					</Button>
				</Grid>

				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Adı"
						required
						fullWidth
						value={step3.adi ?? ""}
						onChange={(e) => setFieldValue("step3.adi", e.target.value)}
						error={getError("adi")}
						helperText={getHelper("adi")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Soyadı"
						required
						fullWidth
						value={step3.soyadi ?? ""}
						onChange={(e) => setFieldValue("step3.soyadi", e.target.value)}
						error={getError("soyadi")}
						helperText={getHelper("soyadi")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>

				{/* Row 2 */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Baba Adı"
						required
						fullWidth
						value={step3.babaAdi ?? ""}
						onChange={(e) => setFieldValue("step3.babaAdi", e.target.value)}
						error={getError("babaAdi")}
						helperText={getHelper("babaAdi")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Anne Adı"
						required
						fullWidth
						value={step3.anneAdi ?? ""}
						onChange={(e) => setFieldValue("step3.anneAdi", e.target.value)}
						error={getError("anneAdi")}
						helperText={getHelper("anneAdi")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>

				{/* Row 3: Doğum İl/İlçe (Menü) */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						id="step3-dogumIlKodu"
						select
						label="Doğum İl/İlçe *"
						fullWidth
						value={step3.dogumIlKodu ?? ""}
						onChange={(e: any) => setFieldValue("step3.dogumIlKodu", e.target.value)}
						error={getError("dogumIlKodu")}
						helperText={getHelper("dogumIlKodu")}
						disabled={!disabeledInputKPS}
					>
						<MenuItem value="">Seçiniz</MenuItem>
						{ilIlceListe.map((option) => (
							<MenuItem key={option.ilIlceKodu} value={option.ilIlceKodu}>
								{option.ilIlceAdi}
							</MenuItem>
						))}
					</TextField>
				</Grid>

				{/* Row 4: Nüfusa Kayıtlı İl/İlçe (Menü), Mahalle */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						id="step3-kayitliIlKodu"
						select
						label="Nüfusa Kayıtlı İl/İlçe *"
						fullWidth
						value={step3.kayitliIlKodu ?? ""}
						onChange={(e: any) => setFieldValue("step3.kayitliIlKodu", e.target.value)}
						error={getError("kayitliIlKodu")}
						helperText={getHelper("kayitliIlKodu")}
						disabled={!disabeledInputKPS}
					>
						<MenuItem value="">Seçiniz</MenuItem>
						{ilIlceListe.map((option) => (
							<MenuItem key={option.ilIlceKodu} value={option.ilIlceKodu}>
								{option.ilIlceAdi}
							</MenuItem>
						))}
					</TextField>
				</Grid>

				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Mahalle / Köy"
						required
						fullWidth
						value={step3.mahalle ?? ""}
						onChange={(e) => setFieldValue("step3.mahalle", e.target.value)}
						error={getError("mahalle")}
						helperText={getHelper("mahalle")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>

				{/* Row 5 */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Nüfus Cüzdan Seri No"
						required
						fullWidth
						value={step3.cilt ?? ""}
						onChange={(e) => setFieldValue("step3.cilt", e.target.value)}
						error={getError("cilt")}
						helperText={getHelper("cilt")}
						disabled={!disabeledInputKPS}
					/>
				</Grid>

				{/* Row 6 */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						select
						label="Dini *"
						value={step3.dini ?? ""}
						onChange={(e: any) => setFieldValue("step3.dini", e.target.value)}
						disabled={!disabeledInputKPS}
						fullWidth
						error={getError("dini")}
						helperText={getHelper("dini")}
					>
						{diniOptions.map((o) => (
							<MenuItem key={o.dini} value={o.dini}>
								{o.dinAciklama}
							</MenuItem>
						))}
					</TextField>
				</Grid>
				<Grid item xs={12} md={3}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label="Nüfus Cüzdan Verildiği Tarih"
							value={parseDateToDayjs(step3.cuzdanVerilisTarihi)}
							onChange={(newValue: Dayjs | null) => {
								setFieldValue("step3.cuzdanVerilisTarihi", newValue ? newValue.format("YYYY-MM-DD") : "");
							}}
							format="DD/MM/YYYY"
							slotProps={{
								textField: {
									fullWidth: true,
									required: true,
									error: getError("cuzdanVerilisTarihi"),
									helperText: getHelper("cuzdanVerilisTarihi"),
									size: "small"
								}
							}}
							disabled={!disabeledInputKPS}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						label="Kayıt No"
						fullWidth
						value={step3.kayitNo ?? ""}
						onChange={(e) => setFieldValue("step3.kayitNo", e.target.value)}
						disabled={!disabeledInputKPS}
					/>
				</Grid>

				{/* Row 7 */}
				<Grid item xs={12} md={3}>
					<TextField
						size='small'
						select
						label="Veriliş Nedeni *"
						value={step3.verilisNedeni ?? ""}
						onChange={(e: any) => setFieldValue("step3.verilisNedeni", e.target.value)}
						disabled={!disabeledInputKPS}
						fullWidth
						error={getError("verilisNedeni")}
						helperText={getHelper("verilisNedeni")}
					>
						{verilisNedeniOptions.map((o) => (
							<MenuItem key={o.nufusCuzdanVerilisNedeni} value={o.nufusCuzdanVerilisNedeni}>
								{o.nufusCuzdanVerilisAciklama}
							</MenuItem>
						))}
					</TextField>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Step3ErkekKimlik;